import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
    onAuthStateChanged, 
    signInWithEmailAndPassword, 
    createUserWithEmailAndPassword, 
    signOut
} from "firebase/auth";
import { 
    doc, 
    onSnapshot, 
    setDoc, 
    updateDoc 
} from "firebase/firestore";
import { auth, db, appId } from '../firebase';
import { getComputedDisplayName } from '../utils/helpers';
import { ADMIN_WHATSAPP_WEBHOOK_URL_1, ADMIN_WHATSAPP_WEBHOOK_URL_2 } from '../utils/constants';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [userDetails, setUserDetails] = useState(null);
    const [loadingAuth, setLoadingAuth] = useState(true);
    const [authErrorMessage, setAuthErrorMessage] = useState('');

    useEffect(() => {
        if (!auth) {
            setLoadingAuth(false);
            return;
        }

        let unsubscribeUser = () => {};

        const unsubscribe = onAuthStateChanged(auth, (user) => {
            unsubscribeUser();
            setCurrentUser(user);
            if (user && !user.isAnonymous) {
                const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid);
                unsubscribeUser = onSnapshot(userDocRef, (doc) => {
                        if (doc.exists()) {
                            const data = doc.data();
                            const details = { 
                                uid: doc.id, 
                                ...data,
                                displayName: getComputedDisplayName(data) 
                            };
                            
                            if (details.isActive === false) {
                                signOut(auth).then(() => {
                                    setAuthErrorMessage("Your account is currently deactivated. Please contact support to re-activate.");
                                });
                                setUserDetails(null);
                                setCurrentUser(null);
                            } else {
                                setUserDetails(details);
                            }

                    } else {
                        const tempDetails = {
                            uid: user.uid,
                            email: user.email,
                            firstName: '',
                            lastName: '',
                            color: '#cccccc',
                            isAdmin: false,
                            pets: [],
                            availableSpots: 0,
                            isActive: true,
                            preferredDisplayName: ''
                        };
                        setUserDetails({
                            ...tempDetails,
                            displayName: getComputedDisplayName(tempDetails)
                        });
                    }
                }, (error) => {
                    console.error("Error fetching user details:", error);
                    setAuthErrorMessage(`Could not load user profile details. Error: ${error.message}`);
                });
            } else {
                setUserDetails(null);
            }
            setLoadingAuth(false);
        });

        return () => {
            unsubscribe();
            unsubscribeUser();
        };
    }, []);

    const value = {
        currentUser,
        userDetails,
        loadingAuth,
        authErrorMessage,
        register: async (email, password, firstName, lastName, color, homeAddress, emergencyContactName, emergencyContactPhone, cellPhoneNumber, preferredDisplayName) => {
            try {
                setAuthErrorMessage('');
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                const user = userCredential.user;
                const userDocRef = doc(db, 'artifacts', appId, 'users', user.uid);
                
                const newUserDetails = {
                    uid: user.uid,
                    email,
                    firstName: firstName || '', 
                    lastName: lastName || '',
                    color,
                    isAdmin: false,
                    userProfilePictureUrl: '',
                    pets: [],
                    homeAddress: homeAddress || '',
                    emergencyContactName: emergencyContactName || '',
                    emergencyContactPhone: emergencyContactPhone || '',
                    cellPhoneNumber: cellPhoneNumber || '',
                    availableSpots: 0,
                    isActive: true, 
                    preferredDisplayName: preferredDisplayName || '', 
                };
                
                newUserDetails.displayName = getComputedDisplayName(newUserDetails); 

                await setDoc(userDocRef, newUserDetails);

                // --- NEW: WhatsApp Notification for New User ---
                if (ADMIN_WHATSAPP_WEBHOOK_URL_1 || ADMIN_WHATSAPP_WEBHOOK_URL_2) {
                    try {
                        const whatsappMessage = `👤 *New User Registration!* \nName: ${firstName} ${lastName}\nEmail: ${email}\nPhone: ${cellPhoneNumber || 'N/A'}`;
                        
                        const sendNotification = async (webhookUrl) => {
                            if (!webhookUrl) return;
                            const separator = webhookUrl.includes('?') ? '&' : '?';
                            const fetchUrl = `${webhookUrl}${separator}text=${encodeURIComponent(whatsappMessage)}`;
                            await fetch(fetchUrl, { mode: 'no-cors' });
                        };

                        await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_1);
                        await sendNotification(ADMIN_WHATSAPP_WEBHOOK_URL_2); 
                        
                        console.log("WhatsApp registration notification sent.");
                    } catch (notifyErr) {
                        console.warn("Failed to trigger WhatsApp notification:", notifyErr);
                    }
                }
                // ----------------------------------------------

                return user;
            } catch (error) {
                if (error.code === 'auth/email-already-in-use') {
                    setAuthErrorMessage('This email address is already in use. Please try logging in instead.');
                } else {
                    setAuthErrorMessage(`Registration failed: ${error.message}`);
                }
                throw error;
            }
        },
        login: async (email, password) => {
            try {
                setAuthErrorMessage('');
                await signInWithEmailAndPassword(auth, email, password);
            } catch (error) {
                setAuthErrorMessage(`Login failed: ${error.message}`);
                throw error;
            }
        },
        logout: async () => {
            try {
                setAuthErrorMessage('');
                await signOut(auth);
            } catch (error) {
                console.error("Error logging out:", error);
                setAuthErrorMessage(`Logout failed: ${error.message}`);
            }
        },
        updateUserProfile: async (profileUpdates) => {
            if (!currentUser || !db) {
                throw new Error("User not authenticated or Firebase not initialized.");
            }
            const userDocRef = doc(db, 'artifacts', appId, 'users', currentUser.uid);
            
            const currentDetails = userDetails;
            const updatedData = {
                ...currentDetails,
                ...profileUpdates,
                displayName: getComputedDisplayName({
                    preferredDisplayName: profileUpdates.preferredDisplayName !== undefined ? profileUpdates.preferredDisplayName : currentDetails.preferredDisplayName,
                    firstName: profileUpdates.firstName || currentDetails.firstName,
                    lastName: profileUpdates.lastName || currentDetails.lastName,
                    email: currentDetails.email,
                })
            };
            
            if (profileUpdates.firstName !== undefined) updatedData.firstName = profileUpdates.firstName;
            if (profileUpdates.lastName !== undefined) updatedData.lastName = profileUpdates.lastName;
            
            await updateDoc(userDocRef, updatedData);
        },
        clearAuthError: () => setAuthErrorMessage('')
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}